/**
 * DiffView Component
 * Shows content changes in a git-diff-like view with accept/reject buttons
 */

import React from 'react'
import { CheckIcon, XMarkIcon } from '@heroicons/react/24/outline'
import clsx from 'clsx'

interface DiffViewProps {
  originalContent: string
  newContent: string
  onAccept: () => void
  onReject: () => void
  isInsertion?: boolean // If true, this is inserting new content (no original)
  title?: string
}

export const DiffView: React.FC<DiffViewProps> = ({
  originalContent,
  newContent,
  onAccept,
  onReject,
  isInsertion = false,
  title = 'Preview Changes'
}) => {
  // For now, show a simple side-by-side or before/after view
  // In a more advanced version, we could use a diff library like diff-match-patch
  
  return (
    <div className="flex flex-col h-full bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-lg">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          {title}
        </h3>
        <div className="flex items-center gap-2">
          <button
            onClick={onReject}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            <XMarkIcon className="w-4 h-4" />
            Reject
          </button>
          <button
            onClick={onAccept}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors"
          >
            <CheckIcon className="w-4 h-4" />
            Accept
          </button>
        </div>
      </div>

      {/* Content Preview */}
      <div className="flex-1 overflow-y-auto p-4">
        {isInsertion ? (
          // Insertion mode: Show only new content with green highlight
          <div className="space-y-2">
            <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-2">
              New Content (to be inserted):
            </div>
            <div className="bg-green-50 dark:bg-green-900/20 border-l-4 border-green-500 p-4 rounded">
              <pre className="whitespace-pre-wrap font-mono text-sm text-gray-900 dark:text-gray-100">
                {newContent}
              </pre>
            </div>
          </div>
        ) : (
          // Replacement mode: Show before/after
          <div className="grid grid-cols-2 gap-4">
            {/* Original */}
            <div className="space-y-2">
              <div className="text-xs font-semibold text-gray-500 dark:text-gray-400">
                Original:
              </div>
              <div className="bg-red-50 dark:bg-red-900/20 border-l-4 border-red-500 p-4 rounded max-h-[60vh] overflow-y-auto">
                <pre className="whitespace-pre-wrap font-mono text-sm text-gray-900 dark:text-gray-100">
                  {originalContent || '(empty)'}
                </pre>
              </div>
            </div>

            {/* New */}
            <div className="space-y-2">
              <div className="text-xs font-semibold text-gray-500 dark:text-gray-400">
                New:
              </div>
              <div className="bg-green-50 dark:bg-green-900/20 border-l-4 border-green-500 p-4 rounded max-h-[60vh] overflow-y-auto">
                <pre className="whitespace-pre-wrap font-mono text-sm text-gray-900 dark:text-gray-100">
                  {newContent}
                </pre>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Footer Info */}
      <div className="px-4 py-2 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 text-xs text-gray-500 dark:text-gray-400">
        {isInsertion ? (
          <span>This content will be inserted at the cursor position.</span>
        ) : (
          <span>Left: Original content | Right: New content</span>
        )}
      </div>
    </div>
  )
}

